import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { HelmetProvider } from "react-helmet-async";
import * as Sentry from "@sentry/react";
import { queryClient } from "./config/queryClient";
import { initSentry } from "./config/sentry";
import { registerAnalyticsHandler } from "./hooks/useAnalytics";
import App from "./App.jsx";
import "./styles/index.css";
import AnnouncementBar from "./pages/customer/AnnouncementBar.jsx";

// ReactQueryDevtools is only imported in development.
// Vite's tree-shaker drops this entire branch in production builds, so the
// devtools bundle (~60 KB) never ships to end-users.
const ReactQueryDevtools = import.meta.env.DEV
  ? (await import("@tanstack/react-query-devtools")).ReactQueryDevtools
  : () => null;

// Fail fast if required environment variables are missing.
// Shows a clear error rather than cryptic network failures throughout the app.
const REQUIRED_ENV = ["VITE_API_BASE_URL", "VITE_RAZORPAY_KEY_ID"];
const missingEnv = REQUIRED_ENV.filter((k) => !import.meta.env[k]);
if (missingEnv.length > 0) {
  document.body.innerHTML = `<pre style="padding:2rem;font-family:monospace;color:red">
Missing required environment variables:\n${missingEnv.map((k) => `  ${k}`).join("\n")}
Add them to your .env file and restart the dev server.
  </pre>`;
  throw new Error(`Missing required env vars: ${missingEnv.join(", ")}`);
}

// Initialise Sentry before rendering so all errors are captured from the start.
// No-op when VITE_SENTRY_DSN is not set.
initSentry();

// Wire useAnalytics() → GA4 so track() calls appear in GA4 as custom events.
// gtag is loaded synchronously in index.html before this module runs.
registerAnalyticsHandler((event, properties) => {
  if (typeof window.gtag === "function") {
    window.gtag("event", event, properties);
  }
});

// Register the service worker after the page has loaded so it never
// competes with the initial resource fetch for bandwidth.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((reg) => {
        // Check for an updated SW in the background every time the page loads.
        reg.update().catch(() => {});

        // Notify the user when a new version is waiting to activate.
        reg.addEventListener("updatefound", () => {
          const incoming = reg.installing;
          if (!incoming) return;
          incoming.addEventListener("statechange", () => {
            if (
              incoming.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              // useSWUpdate.js in App.jsx handles the update toast — no action needed here.
              console.info(
                "[SW] New version available — useSWUpdate will prompt.",
              );
            }
          });
        });
      })
      .catch((err) => {
        // SW registration failures are non-fatal — app works normally.
        console.warn("[SW] Registration failed:", err);
      });
  });
}

createRoot(document.getElementById("root")).render(
  <StrictMode>
    {/* Sentry ErrorBoundary wraps the whole app so unhandled render errors
        are captured and users see a friendly fallback instead of a blank page */}
    <Sentry.ErrorBoundary
      fallback={
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <h2>Something went wrong</h2>
          <p>Our team has been notified. Please refresh the page.</p>
        </div>
      }
      showDialog
    >
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            {/* <AnnouncementBar /> */}
            <App />
          </BrowserRouter>
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 3000,
              style: { fontSize: "14px", borderRadius: "12px" },
              success: { iconTheme: { primary: "#16a34a", secondary: "#fff" } },
              error: { iconTheme: { primary: "#dc2626", secondary: "#fff" } },
            }}
          />
          <ReactQueryDevtools
            initialIsOpen={false}
            buttonPosition="bottom-left"
          />
        </QueryClientProvider>
      </HelmetProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
